// src/checkout/services/validationHelpers.ts
import { z } from "zod";
function deepStrict(schema) {
  if (schema instanceof z.ZodObject) {
    const strictShape = Object.fromEntries(
      Object.entries(schema.shape).map(([k, v]) => [
        k,
        deepStrict(v)
      ])
    );
    return schema.extend(strictShape).strict();
  }
  if (schema instanceof z.ZodArray) {
    return z.array(deepStrict(schema.element));
  }
  if (schema instanceof z.ZodOptional) {
    return deepStrict(schema.unwrap()).optional();
  }
  if (schema instanceof z.ZodNullable) {
    return deepStrict(schema.unwrap()).nullable();
  }
  if (schema instanceof z.ZodUnion) {
    return z.union(
      schema.options.map(deepStrict)
    );
  }
  return schema;
}
function toAdminMessage(technical) {
  var _a, _b;
  const match = technical.match(/^\[([^\]]+)\]\s*(.+)$/);
  if (!match) return technical;
  const [, rawPath, message] = match;
  const parts = rawPath.split(".");
  const context = [];
  let i = 0;
  while (i < parts.length) {
    const part = parts[i];
    if (part === "cms" || part === "crm") {
      context.push(part.toUpperCase());
    } else if (part === "sections") {
      const indexPart = (_a = parts[++i]) != null ? _a : "";
      const m = indexPart.match(/^(\d+)\(([^)]+)\)$/);
      if (m) context.push(`Section ${Number(m[1]) + 1} (${m[2]})`);
      else if (indexPart) context.push(`Section ${Number(indexPart) + 1}`);
    } else if (part === "elements") {
      const indexPart = (_b = parts[++i]) != null ? _b : "";
      const m = indexPart.match(/^(\d+)\(([^)]+)\)$/);
      if (m) context.push(`element ${Number(m[1]) + 1} (${m[2]})`);
      else if (indexPart) context.push(`element ${Number(indexPart) + 1}`);
    } else if (part === "slots") {
      i++;
    } else if (part === "products") {
    } else if (part === "baseProducts" || part === "subscriptionProducts") {
      const next = parts[i + 1];
      if (next !== void 0 && !isNaN(Number(next))) {
        context.push(
          `${part === "baseProducts" ? "Base" : "Subscription"} product ${Number(next) + 1}`
        );
        i++;
      } else {
        context.push(part === "baseProducts" ? "Base products" : "Subscription products");
      }
    } else if (part === "productVariants") {
      const key = parts[i + 1];
      if (key && !key.includes(".")) {
        context.push(`Variant group "${key}"`);
        i++;
      } else {
        context.push("Product variants");
      }
    } else if (!isNaN(Number(part))) {
      context.push(`item ${Number(part) + 1}`);
    } else if (part && part !== "content" && part !== "config" && part !== "styles" && part !== "slots" && part !== "variants") {
      context.push(part);
    }
    i++;
  }
  const prefix = context.join(" \u203A ");
  const simplifiedMessage = message.replace(/^Unrecognized key:\s*"([^"]+)"/, 'unknown field "$1"').replace(/^Invalid input: expected\s*"([^"]+)"/, 'must be "$1"').replace(/^Invalid input: expected\s*(.+)/, "must be $1").replace(/^Invalid input$/, "invalid value");
  return prefix ? `${prefix}: ${simplifiedMessage}` : simplifiedMessage;
}
function makeErrors(technical) {
  return technical.map((t) => ({ technical: t, admin: toAdminMessage(t) }));
}

// src/checkout/configs/validateCheckout.ts
import { z as z3 } from "zod";

// src/checkout/configs/checkoutConfigSchemas.generated.ts
import { z as z2 } from "zod";

// src/checkout/validationTypes.ts
var EnabledVariantTypes = /* @__PURE__ */ ((EnabledVariantTypes2) => {
  EnabledVariantTypes2["PER_PRODUCT"] = "perProduct";
  EnabledVariantTypes2["PER_CHECKOUT"] = "perCheckout";
  return EnabledVariantTypes2;
})(EnabledVariantTypes || {});
var ProductVisibilityContexts = /* @__PURE__ */ ((ProductVisibilityContexts2) => {
  ProductVisibilityContexts2["PRODUCT_LISTS"] = "productLists";
  ProductVisibilityContexts2["SPECIAL_OFFER_MODAL"] = "specialOfferModal";
  return ProductVisibilityContexts2;
})(ProductVisibilityContexts || {});
var PRODUCT_HERO_MEDIA_KIND = {
  image: "image",
  video: "video",
  iframe: "iframe"
};

// src/checkout/configs/checkoutConfigSchemas.generated.ts
var enabledVariantTypesSchema = z2.enum(EnabledVariantTypes);
var enabledVariantTypeSchema = z2.union([
  z2.literal("perProduct"),
  z2.literal("perCheckout")
]);
var crmProductVariantPickerOptionSchema = z2.object({
  id: z2.string(),
  value: z2.string(),
  isDefaultVariant: z2.boolean().optional(),
  image: z2.string().optional()
});
var cmsProductVariantPickerOptionSchema = z2.object({
  id: z2.string(),
  color: z2.string().optional(),
  label: z2.string()
});
var productVariantLinkSchema = z2.object({
  label: z2.string(),
  href: z2.string(),
  image: z2.string().optional()
});
var crmProductVariantGroupConfigSchema = z2.object({
  variants: z2.array(crmProductVariantPickerOptionSchema)
});
var cmsProductVariantGroupConfigSchema = z2.object({
  customVariantsSelectionTitle: z2.string(),
  customVariantsItemLabel: z2.string(),
  customVariantLink: productVariantLinkSchema.optional(),
  variants: z2.array(cmsProductVariantPickerOptionSchema)
});
var crmProductVariantsConfigSchema = z2.record(
  z2.string(),
  crmProductVariantGroupConfigSchema
);
var cmsProductVariantsConfigSchema = z2.record(
  z2.string(),
  cmsProductVariantGroupConfigSchema
);
var combinedVariantItemSchema = z2.object({
  konnVariantId: z2.string(),
  attributes: z2.record(z2.string(), z2.string()),
  imageUrl: z2.string()
});
var productAdditionalProductDataSchema = z2.record(z2.string(), z2.union([z2.string(), z2.number(), z2.boolean(), z2.undefined()]).nullable()).and(
  z2.object({
    custom: z2.union([z2.number(), z2.string()]).optional()
  })
);
var productVariantConfigSchema = z2.object({
  konnVariantId: z2.string(),
  title: z2.string(),
  sku: z2.string(),
  price: z2.number(),
  shippingPrice: z2.number(),
  attributes: z2.record(z2.string(), z2.string()),
  imageUrl: z2.string(),
  isOutOfStock: z2.boolean(),
  stockQuantity: z2.number(),
  isActive: z2.boolean(),
  sortOrder: z2.number()
});
var crmCheckoutProductConfigItemSchema = z2.object({
  id: z2.union([z2.number(), z2.string()]),
  name: z2.string(),
  amount: z2.number(),
  price: z2.number(),
  shippingPrice: z2.number(),
  imageUrl: z2.string(),
  warrantyPrice: z2.number().optional().nullable(),
  oldPrice: z2.number().optional().nullable(),
  promoDiscountedPrice: z2.number().optional().nullable(),
  promoDiscountAmountPercent: z2.number().optional().nullable(),
  youSavePercentage: z2.number().optional().nullable(),
  billedAmount: z2.number().optional().nullable(),
  sku: z2.string().optional(),
  promoShippingPrice: z2.number().optional().nullable(),
  additionalProductData: productAdditionalProductDataSchema.optional(),
  nextPageOnSuccess: z2.string().optional()
});
var productVisibilityContextsSchema = z2.enum(ProductVisibilityContexts);
var productVisibilityContextSchema = z2.union([
  z2.literal("productLists"),
  z2.literal("specialOfferModal")
]);
var cmsCheckoutProductConfigItemSchema = z2.object({
  id: z2.union([z2.number(), z2.string()]),
  description: z2.string().optional(),
  isBestSeller: z2.boolean().optional(),
  isBestValue: z2.boolean().optional(),
  additionalProductData: productAdditionalProductDataSchema.optional(),
  visibility: z2.array(productVisibilityContextSchema).optional()
});
var productConfigItemSchema = crmCheckoutProductConfigItemSchema.and(
  cmsCheckoutProductConfigItemSchema
);
var checkoutFormPaymentMethodsConfigSchema = z2.object({
  klarna: z2.boolean().optional(),
  applePay: z2.boolean().optional(),
  googlePay: z2.boolean().optional()
});
var checkoutFormLabelsConfigSchema = z2.object({
  firstName: z2.string().optional(),
  lastName: z2.string().optional(),
  email: z2.string().optional(),
  phone: z2.string().optional(),
  country: z2.string().optional(),
  address: z2.string().optional(),
  city: z2.string().optional(),
  state: z2.string().optional(),
  zip: z2.string().optional(),
  cardNumber: z2.string().optional(),
  expiration: z2.string().optional(),
  securityCode: z2.string().optional(),
  securityComment: z2.string().optional(),
  policy: z2.string().optional(),
  search: z2.string().optional()
});
var checkoutFormHeadingsConfigSchema = z2.object({
  customerInformation: z2.string().optional(),
  deliveryAddress: z2.string().optional(),
  paymentMethods: z2.string().optional(),
  paymentSubtext: z2.string().optional(),
  creditCard: z2.string().optional(),
  paypal: z2.string().optional(),
  paypalInner: z2.string().optional(),
  klarna: z2.string().optional(),
  applePay: z2.string().optional(),
  googlePay: z2.string().optional(),
  expressTop: z2.string().optional(),
  expressBottom: z2.string().optional(),
  sslText: z2.string().optional(),
  submit: z2.string().optional(),
  processing: z2.string().optional(),
  paypalSubmit: z2.string().optional()
});
var checkoutFormErrorsConfigSchema = z2.object({
  required: z2.string().optional(),
  email: z2.string().optional(),
  phone: z2.string().optional(),
  card: z2.string().optional(),
  expiration: z2.string().optional(),
  cvv: z2.string().optional(),
  policy: z2.string().optional(),
  symbol: z2.string().optional(),
  invalidCountry: z2.string().optional()
});
var paymentsConfigSchema = z2.object({
  isAdyenEnabled: z2.boolean().optional(),
  paypalMerchant: z2.string().optional(),
  gpayGateway: z2.string().optional(),
  gpayGatewayMerchant: z2.string().optional(),
  gpayMerchantId: z2.string().optional(),
  googlePayConfigured: z2.boolean().optional(),
  applePayConfigured: z2.boolean().optional(),
  stripePublisherKey: z2.string().optional(),
  stripeApplePayAllowed: z2.boolean().optional(),
  klarnaAllowed: z2.boolean().optional(),
  checkoutcomPublisherKey: z2.string().optional(),
  dynamicDescriptor: z2.string().optional(),
  removeWarrantyWithKlarna: z2.boolean().optional(),
  requirePolicy: z2.boolean().optional(),
  hidePaypalExpressButton: z2.boolean().optional(),
  paymentMethods: checkoutFormPaymentMethodsConfigSchema.optional()
});
var productDropdownContentConfigSchema = z2.object({
  title: z2.string().optional(),
  message: z2.string().optional()
});
var footerLinkSchema = z2.object({
  label: z2.string(),
  href: z2.string()
});
var productHeroMediaKindSchema = z2.union([
  z2.literal("image"),
  z2.literal("video"),
  z2.literal("iframe")
]);
var productHeroBenefitSchema = z2.object({
  id: z2.string(),
  label: z2.string()
});
var productHeroSellingPointSchema = z2.object({
  id: z2.string(),
  iconSrc: z2.string(),
  iconAlt: z2.string().optional(),
  label: z2.string()
});
var productHeroSocialProofSchema = z2.object({
  customersValue: z2.string().optional(),
  customersLabel: z2.string().optional(),
  ratingValue: z2.string().optional(),
  ratingLabel: z2.string().optional()
});
var productHeroMediaItemSchema = z2.object({
  id: z2.string(),
  kind: productHeroMediaKindSchema,
  src: z2.string(),
  thumbnailSrc: z2.string(),
  alt: z2.string().optional(),
  posterSrc: z2.string().optional(),
  autoplay: z2.boolean().optional(),
  loop: z2.boolean().optional()
});
var heroFlagRowElementSchema = z2.object({
  type: z2.literal("flag-row"),
  content: z2.object({
    label: z2.string(),
    icon: z2.string().optional(),
    iconAlt: z2.string().optional()
  })
});
var heroHeadingDescriptionElementSchema = z2.object({
  type: z2.literal("heading-description"),
  content: z2.object({
    heading: z2.string().optional(),
    description: z2.string().optional()
  })
});
var heroRatingRowElementSchema = z2.object({
  type: z2.literal("rating-row"),
  content: z2.object({
    rating: z2.number(),
    ratingText: z2.string().optional(),
    reviewsLabel: z2.string().optional(),
    isTrustpilot: z2.boolean().optional()
  })
});
var heroBenefitElementSchema = z2.object({
  type: z2.literal("benefit"),
  content: z2.object({
    label: z2.string()
  })
});
var heroSocialProofElementSchema = z2.object({
  type: z2.literal("social-proof"),
  content: productHeroSocialProofSchema
});
var heroSellingPointsElementSchema = z2.object({
  type: z2.literal("selling-points"),
  content: z2.object({
    sellingPoints: z2.array(productHeroSellingPointSchema)
  })
});
var heroProductCardListElementSchema = z2.object({
  type: z2.literal("product-card-list"),
  content: z2.object({
    eachLabel: z2.string(),
    bestValueLabel: z2.string().optional(),
    bestSellerLabel: z2.string().optional()
  })
});
var heroCtaElementSchema = z2.object({
  type: z2.literal("cta"),
  content: z2.object({
    text: z2.string(),
    subActionText: z2.string().optional()
  })
});
var layoutBarElementSchema = z2.object({
  type: z2.literal("bar"),
  config: z2.object({
    type: z2.union([
      z2.literal("progress"),
      z2.literal("timer"),
      z2.literal("text"),
      z2.literal("freeShipping")
    ]),
    icon: z2.string().optional(),
    iconAlt: z2.string().optional(),
    duration: z2.number().optional(),
    storageKey: z2.string().optional(),
    centered: z2.boolean().optional(),
    compact: z2.boolean().optional()
  }),
  content: z2.object({
    text: z2.string().optional(),
    textLeft: z2.string().optional(),
    textRight: z2.string().optional()
  }),
  styles: z2.object({
    bgColor: z2.string().optional(),
    textColor: z2.string().optional(),
    borderColor: z2.string().optional(),
    meterColor: z2.string().optional(),
    stockCountColor: z2.string().optional()
  }).optional()
});
var heroSectionElementSchema = z2.union([
  heroFlagRowElementSchema,
  heroHeadingDescriptionElementSchema,
  heroRatingRowElementSchema,
  heroBenefitElementSchema,
  heroSocialProofElementSchema,
  heroSellingPointsElementSchema,
  heroProductCardListElementSchema,
  heroCtaElementSchema,
  layoutBarElementSchema
]);
var heroSectionSlotSchema = z2.object({
  name: z2.literal("content"),
  elements: z2.array(heroSectionElementSchema)
});
var layoutSubscriptionSelectElementSchema = z2.object({
  type: z2.literal("subscription-select"),
  content: z2.object({
    buttonSubscription: z2.string(),
    buttonBase: z2.string(),
    footerTextSubscriptionEnabled: z2.string().optional(),
    footerTextSubscriptionDisabled: z2.string().optional()
  })
});
var layoutProductListElementSchema = z2.object({
  type: z2.literal("product-list"),
  content: z2.object({
    productsHeading: z2.string(),
    pickerAdditionalMessage: z2.string().optional(),
    productEachLabel: z2.string().optional(),
    discountSaveLabel: z2.string().optional(),
    discountOffLabel: z2.string().optional(),
    productBestSellerLabel: z2.string().optional(),
    productBestValueLabel: z2.string().optional()
  })
});
var layoutSubscriptionToggleBoxElementSchema = z2.object({
  type: z2.literal("subscription-toggle-box"),
  content: z2.object({
    title: z2.string(),
    subtitleSubscriptionEnabled: z2.string(),
    subtitleSubscriptionDisabled: z2.string()
  })
});
var layoutOrderFormElementSchema = z2.object({
  type: z2.literal("order-form"),
  config: z2.object({
    requirePolicy: z2.boolean().optional(),
    hidePaypalExpressButton: z2.boolean().optional(),
    paymentMethods: checkoutFormPaymentMethodsConfigSchema.optional()
  }).optional(),
  content: z2.object({
    defaultPaymentMethod: z2.string().optional(),
    paymentMethodPosition: z2.union([z2.literal("top"), z2.literal("bottom"), z2.string()]).optional(),
    defaultCountry: z2.string().optional(),
    disclaimerText: z2.string().optional(),
    savingsStyle: z2.string().optional(),
    warrantyLabel: z2.string().optional(),
    shippingProtectionLabel: z2.string().optional(),
    discountError: z2.string().optional(),
    discountExpired: z2.string().optional(),
    productList: z2.object({
      productsHeading: z2.string().optional(),
      pickerAdditionalMessage: z2.string().optional(),
      productEachLabel: z2.string().optional(),
      discountSaveLabel: z2.string().optional(),
      discountOffLabel: z2.string().optional(),
      productBestSellerLabel: z2.string().optional(),
      productBestValueLabel: z2.string().optional()
    }).optional(),
    subscription: z2.object({
      title: z2.string().optional(),
      subscriptionDescription: z2.string().optional(),
      oneTimeDescription: z2.string().optional(),
      subscribeAndSaveLabel: z2.string().optional(),
      oneTimePurchaseLabel: z2.string().optional()
    }).optional(),
    summary: z2.object({
      title: z2.string().optional(),
      discountApplyLabel: z2.string().optional(),
      warrantyCheckboxLabelTemplate: z2.string().optional(),
      totalSavingsLabel: z2.string().optional(),
      warrantySelectedLabel: z2.string().optional(),
      subtotalLabel: z2.string().optional(),
      shippingLabel: z2.string().optional(),
      totalLabel: z2.string().optional(),
      discountPlaceholder: z2.string().optional(),
      discountErrorLabel: z2.string().optional(),
      isSavingsDisplayedWhenClosed: z2.boolean().optional(),
      isExpanded: z2.boolean().optional()
    }).optional(),
    labels: checkoutFormLabelsConfigSchema.optional(),
    headings: checkoutFormHeadingsConfigSchema.optional(),
    errors: checkoutFormErrorsConfigSchema.optional()
  })
});
var layoutFooterElementSchema = z2.object({
  type: z2.literal("footer"),
  config: z2.object({
    showOnDesktop: z2.boolean().optional()
  }),
  content: z2.object({
    links: z2.array(footerLinkSchema)
  })
});
var layoutOrderSummaryElementSchema = z2.object({
  type: z2.literal("order-summary"),
  config: z2.object({
    isDiscountInputEnabled: z2.boolean().optional(),
    isWarrantyToggleEnabled: z2.boolean().optional(),
    isSavingsDisplayedWhenClosed: z2.boolean().optional(),
    isExpanded: z2.boolean().optional()
  }),
  content: z2.object({
    title: z2.string(),
    totalSavingsLabel: z2.string(),
    subtotalLabel: z2.string(),
    shippingLabel: z2.string(),
    totalLabel: z2.string(),
    discountApplyLabel: z2.string().optional(),
    discountPlaceholder: z2.string().optional(),
    discountErrorLabel: z2.string().optional(),
    warrantyCheckboxLabelTemplate: z2.string().optional(),
    warrantySelectedLabel: z2.string().optional()
  })
});
var layoutBadgeCardElementSchema = z2.object({
  type: z2.literal("badge-card"),
  content: z2.object({
    icon: z2.string().optional(),
    title: z2.string(),
    description: z2.string()
  })
});
var layoutTextSeparatorElementSchema = z2.object({
  type: z2.literal("text-separator"),
  content: z2.object({
    text: z2.string()
  })
});
var layoutFeatureCardElementSchema = z2.object({
  type: z2.literal("feature-card"),
  config: z2.record(z2.string(), z2.never()).optional(),
  content: z2.object({
    icon: z2.string().optional(),
    iconAlt: z2.string().optional(),
    title: z2.string().optional(),
    description: z2.string().optional()
  }).optional()
});
var layoutRatingCardElementSchema = z2.object({
  type: z2.literal("rating-card"),
  config: z2.object({
    rating: z2.number().optional()
  }).optional(),
  content: z2.object({
    title: z2.string(),
    description: z2.string(),
    name: z2.string().optional(),
    label: z2.string().optional(),
    isTrustpilot: z2.boolean().optional()
  })
});
var layoutSectionElementSchema = z2.union([
  layoutBarElementSchema,
  layoutSubscriptionSelectElementSchema,
  layoutProductListElementSchema,
  layoutSubscriptionToggleBoxElementSchema,
  layoutOrderFormElementSchema,
  layoutFooterElementSchema,
  layoutOrderSummaryElementSchema,
  layoutBadgeCardElementSchema,
  layoutTextSeparatorElementSchema,
  layoutFeatureCardElementSchema,
  layoutRatingCardElementSchema
]);
var layoutSectionSlotSchema = z2.object({
  name: z2.union([z2.literal("col-1"), z2.literal("col-2")]),
  elements: z2.array(layoutSectionElementSchema)
});
var ribbonSectionStylesSchema = z2.object({
  bgColor: z2.string().optional(),
  textColor: z2.string().optional()
});
var sectionRibbonSchema = z2.object({
  type: z2.literal("ribbon"),
  content: z2.object({
    message: z2.string(),
    iconImage: z2.string().optional(),
    iconAlt: z2.string().optional()
  }),
  styles: ribbonSectionStylesSchema.optional()
});
var sectionTimerRibbonSchema = z2.object({
  type: z2.literal("ribbon-timer"),
  config: z2.object({
    durationMinutes: z2.number(),
    storageKey: z2.string().optional()
  }),
  content: z2.object({
    message: z2.string(),
    labelTemplate: z2.string().optional(),
    iconImage: z2.string().optional(),
    iconAlt: z2.string().optional()
  }),
  styles: ribbonSectionStylesSchema.and(
    z2.object({
      timerTextColor: z2.string().optional()
    })
  ).optional()
});
var sectionHeaderSchema = z2.object({
  type: z2.literal("header"),
  content: z2.object({
    shoppingBagLabel: z2.string().optional()
  })
});
var sectionInlineBreadcrumbHeaderSchema = z2.object({
  type: z2.literal("header-inline-breadcrumb"),
  config: z2.object({
    activeIndex: z2.number()
  }),
  content: z2.object({
    steps: z2.array(
      z2.object({
        label: z2.string()
      })
    )
  })
});
var sectionPillBreadcrumbHeaderSchema = z2.object({
  type: z2.literal("header-pill-breadcrumb"),
  config: z2.object({
    activeIndex: z2.number()
  }),
  content: z2.object({
    steps: z2.array(
      z2.object({
        label: z2.string()
      })
    )
  })
});
var sectionHeroSchema = z2.object({
  type: z2.literal("hero-section"),
  config: z2.object({
    thumbnailsBelow: z2.boolean().optional()
  }),
  content: z2.object({
    media: z2.array(productHeroMediaItemSchema)
  }),
  slots: z2.array(heroSectionSlotSchema)
});
var sectionSeparatorSchema = z2.object({
  type: z2.literal("separator-v1"),
  config: z2.object({
    height: z2.number().optional(),
    borderless: z2.boolean().optional()
  }).optional()
});
var sectionLayout2ColV1Schema = z2.object({
  type: z2.literal("layout-2col-v1"),
  config: z2.object({
    isReversed: z2.boolean().optional()
  }),
  slots: z2.array(layoutSectionSlotSchema)
});
var sectionBenefitsSchema = z2.object({
  type: z2.literal("benefits"),
  config: z2.object({
    showUnderline: z2.boolean().optional(),
    underlineAlign: z2.union([z2.literal("left"), z2.literal("center"), z2.literal("right")]).optional()
  }).optional(),
  content: z2.object({
    title: z2.string().optional(),
    ctaText: z2.string().optional(),
    subActionText: z2.string().optional(),
    columns: z2.array(
      z2.object({
        title: z2.string().optional(),
        featured: z2.boolean().optional(),
        contrastBorder: z2.boolean().optional(),
        items: z2.array(
          z2.object({
            text: z2.string(),
            positive: z2.boolean().optional()
          })
        )
      })
    )
  }),
  styles: z2.object({
    bgColor: z2.string().optional(),
    separatorColor: z2.string().optional(),
    cardBgColor: z2.string().optional(),
    cardTitleColor: z2.string().optional(),
    itemTextColor: z2.string().optional(),
    checkIconColor: z2.string().optional(),
    crossIconColor: z2.string().optional()
  }).optional()
});
var sectionFeatureCardsSectionSchema = z2.object({
  type: z2.literal("feature-cards-section"),
  config: z2.object({
    showUnderline: z2.boolean().optional(),
    underlineAlign: z2.union([z2.literal("left"), z2.literal("center"), z2.literal("right")]).optional()
  }),
  content: z2.object({
    title: z2.string(),
    items: z2.array(
      z2.object({
        image: z2.string(),
        title: z2.string(),
        description: z2.string().optional()
      })
    )
  }),
  styles: z2.object({
    bgColor: z2.string().optional(),
    separatorColor: z2.string().optional(),
    titleColor: z2.string().optional(),
    textColor: z2.string().optional()
  }).optional()
});
var sectionFeatureImageCardListSchema = z2.object({
  type: z2.literal("feature-image-card-list"),
  content: z2.object({
    items: z2.array(
      z2.object({
        image: z2.string(),
        title: z2.string(),
        description: z2.string().optional()
      })
    )
  }),
  styles: z2.object({
    bgColor: z2.string().optional(),
    titleColor: z2.string().optional(),
    textColor: z2.string().optional()
  }).optional()
});
var sectionCtaInlineSchema = z2.object({
  type: z2.literal("cta-inline"),
  content: z2.object({
    rating: z2.number(),
    ctaText: z2.string(),
    stockText: z2.string().optional(),
    ratingText: z2.string().optional(),
    reviewsText: z2.string().optional(),
    isTrustpilot: z2.boolean().optional(),
    subActionText: z2.string().optional()
  }),
  styles: z2.object({
    bgColor: z2.string().optional(),
    borderColor: z2.string().optional(),
    pillRatingColor: z2.string().optional(),
    pillReviewsColor: z2.string().optional(),
    ctaBgColor: z2.string().optional(),
    ctaTextColor: z2.string().optional()
  }).optional()
});
var sectionCtaImageSchema = z2.object({
  type: z2.literal("cta-image"),
  config: z2.object({
    showUnderline: z2.boolean().optional(),
    underlineAlign: z2.union([z2.literal("left"), z2.literal("center"), z2.literal("right")]).optional()
  }).optional(),
  content: z2.object({
    title: z2.string(),
    productImage: z2.string(),
    rating: z2.number(),
    ctaText: z2.string(),
    ratingText: z2.string().optional(),
    description: z2.string().optional(),
    reviewsText: z2.string().optional(),
    isTrustpilot: z2.boolean().optional(),
    subActionText: z2.string().optional()
  }),
  styles: z2.object({
    bgColor: z2.string().optional(),
    borderColor: z2.string().optional(),
    textColor: z2.string().optional(),
    separatorColor: z2.string().optional(),
    separatorEndColor: z2.string().optional()
  }).optional()
});
var sectionCtaImageV2Schema = z2.object({
  type: z2.literal("cta-image-v2"),
  config: z2.object({
    showUnderline: z2.boolean().optional()
  }).optional(),
  content: z2.object({
    title: z2.string(),
    ctaText: z2.string(),
    productImage: z2.string(),
    description: z2.string().optional(),
    priceLine: z2.string().optional(),
    formattedPrice: z2.string().optional(),
    productImageWide: z2.string().optional(),
    productImageAlt: z2.string().optional(),
    subActionText: z2.string().optional(),
    backgroundImage: z2.string().optional(),
    features: z2.array(
      z2.object({
        label: z2.string()
      })
    ).optional()
  }),
  styles: z2.object({
    bgColor: z2.string().optional(),
    titleColor: z2.string().optional(),
    featureIconColor: z2.string().optional(),
    featureTextColor: z2.string().optional(),
    ctaBgColor: z2.string().optional(),
    ctaTextColor: z2.string().optional(),
    separatorColor: z2.string().optional(),
    separatorEndColor: z2.string().optional()
  }).optional()
});
var sectionCtaTimerSchema = z2.object({
  type: z2.literal("cta-timer"),
  config: z2.object({
    durationMinutes: z2.number(),
    storageKey: z2.string().optional(),
    showUnderline: z2.boolean().optional(),
    underlineAlign: z2.union([z2.literal("left"), z2.literal("center"), z2.literal("right")]).optional()
  }),
  content: z2.object({
    title: z2.string(),
    rating: z2.number(),
    timerLabel: z2.string(),
    ctaText: z2.string(),
    description: z2.string().optional(),
    ratingText: z2.string().optional(),
    isTrustpilot: z2.boolean().optional(),
    subActionText: z2.string().optional()
  }),
  styles: z2.object({
    bgColor: z2.string().optional(),
    textColor: z2.string().optional(),
    itemBorderColor: z2.string().optional(),
    itemBgColor: z2.string().optional(),
    itemTextColor: z2.string().optional(),
    timeBgColor: z2.string().optional()
  }).optional()
});
var sectionFeaturesSchema = z2.object({
  type: z2.literal("features"),
  content: z2.object({
    title: z2.string(),
    ctaText: z2.string(),
    productImage: z2.string(),
    features: z2.array(
      z2.object({
        label: z2.string(),
        description: z2.string().optional()
      })
    ),
    subActionText: z2.string().optional()
  }),
  config: z2.object({
    showUnderline: z2.boolean().optional(),
    underlineAlign: z2.union([z2.literal("left"), z2.literal("center"), z2.literal("right")]).optional()
  }).optional(),
  styles: z2.object({
    bgColor: z2.string().optional(),
    titleColor: z2.string().optional(),
    separatorColor: z2.string().optional(),
    textColor: z2.string().optional()
  }).optional()
});
var footerContactItemTypeSchema = z2.union([
  z2.literal("email"),
  z2.literal("chat"),
  z2.literal("phone")
]);
var footerPaymentIconTypeSchema = z2.union([
  z2.literal("paypal"),
  z2.literal("visa"),
  z2.literal("mastercard"),
  z2.literal("amex"),
  z2.literal("diners"),
  z2.literal("discover")
]);
var sectionSpecialOfferModalSchema = z2.object({
  type: z2.literal("special-offer-modal"),
  config: z2.object({
    initiallyOpen: z2.boolean().optional()
  }).optional(),
  content: z2.object({
    title: z2.string(),
    ctaText: z2.string(),
    eachLabel: z2.string().optional(),
    bestValueLabel: z2.string().optional(),
    bestSellerLabel: z2.string().optional(),
    bars: z2.array(layoutBarElementSchema).optional(),
    subActionText: z2.string().optional()
  }),
  styles: z2.object({
    bgColor: z2.string().optional(),
    textColor: z2.string().optional(),
    titleColor: z2.string().optional(),
    barsBgColor: z2.string().optional()
  }).optional()
});
var crmCheckoutConfigSchema = z2.object({
  formPrefillData: z2.record(z2.string(), z2.string()).optional(),
  paymentsConfig: paymentsConfigSchema.optional(),
  products: z2.object({
    combinedVariants: z2.array(combinedVariantItemSchema).optional(),
    productVariants: crmProductVariantsConfigSchema.optional(),
    baseProducts: z2.array(crmCheckoutProductConfigItemSchema).optional(),
    subscriptionProducts: z2.array(crmCheckoutProductConfigItemSchema).optional()
  }),
  isSubscriptionEnabled: z2.boolean(),
  isDiscountCodeFeatureEnabled: z2.boolean().optional(),
  discountCode: z2.string().optional(),
  selectedProductIndex: z2.number().optional(),
  enabledVariantType: enabledVariantTypeSchema.optional(),
  isWarrantyEnabled: z2.boolean(),
  isWarrantyPreselected: z2.boolean(),
  shouldMultiplyWarrantyByQuantity: z2.boolean(),
  currencyCode: z2.string(),
  aff: z2.string().optional(),
  country: z2.string().optional(),
  postId: z2.string().optional(),
  nextPageOnSuccess: z2.string().optional(),
  konnektiveCampaignId: z2.string().optional(),
  subscriptionNextPage: z2.string().optional(),
  subscriptionCampaignId: z2.string().optional()
});
var reviewsSmallImagesVerticalBlockSchema = z2.any();
var footerNavColumnDefSchema = z2.any();
var themeVariantSchema = z2.any();
var sectionReviewsSchema = z2.object({
  type: z2.literal("reviews"),
  block: reviewsSmallImagesVerticalBlockSchema
});
var sectionFooterSchema = z2.object({
  type: z2.literal("footer"),
  content: z2.object({
    logo: z2.string().optional(),
    logoAlt: z2.string().optional(),
    description: z2.string().optional(),
    disclaimer: z2.string().optional(),
    copyrightText: z2.string().optional(),
    columns: z2.array(footerNavColumnDefSchema).optional(),
    contactTitle: z2.string().optional(),
    contactItems: z2.array(
      z2.object({
        type: footerContactItemTypeSchema,
        label: z2.string(),
        href: z2.string().optional()
      })
    ).optional(),
    paymentIconTypes: z2.array(footerPaymentIconTypeSchema).optional()
  }),
  config: z2.object({
    showPaymentIcons: z2.boolean().optional(),
    showContactColumn: z2.boolean().optional(),
    showBenefits: z2.boolean().optional(),
    showLogo: z2.boolean().optional(),
    showDescription: z2.boolean().optional(),
    showColumns: z2.boolean().optional(),
    showDisclaimer: z2.boolean().optional(),
    showCopyrightBar: z2.boolean().optional()
  }).optional(),
  styles: z2.object({
    footerBackground: z2.string().optional(),
    copyrightBackground: z2.string().optional(),
    headingColor: z2.string().optional(),
    linkColor: z2.string().optional(),
    linkHoverColor: z2.string().optional(),
    descriptionColor: z2.string().optional(),
    dividerColor: z2.string().optional(),
    disclaimerColor: z2.string().optional(),
    copyrightColor: z2.string().optional()
  }).optional()
});
var cmsCheckoutSectionSchema = z2.union([
  sectionRibbonSchema,
  sectionTimerRibbonSchema,
  sectionHeaderSchema,
  sectionInlineBreadcrumbHeaderSchema,
  sectionPillBreadcrumbHeaderSchema,
  sectionHeroSchema,
  sectionSeparatorSchema,
  sectionLayout2ColV1Schema,
  sectionBenefitsSchema,
  sectionFeatureCardsSectionSchema,
  sectionFeatureImageCardListSchema,
  sectionCtaInlineSchema,
  sectionCtaImageSchema,
  sectionCtaImageV2Schema,
  sectionCtaTimerSchema,
  sectionReviewsSchema,
  sectionFeaturesSchema,
  sectionFooterSchema,
  sectionSpecialOfferModalSchema
]);
var cmsCheckoutConfigSchema = z2.object({
  theme: themeVariantSchema,
  logo: z2.object({
    src: z2.string(),
    alt: z2.string().optional()
  }).optional(),
  isCleanVersion: z2.boolean().optional(),
  isPreview: z2.boolean().optional(),
  products: z2.object({
    formattedString: z2.string(),
    showProductPickerV2: z2.boolean().optional(),
    productDropdownContent: productDropdownContentConfigSchema.optional(),
    productVariants: cmsProductVariantsConfigSchema.optional(),
    baseProducts: z2.array(cmsCheckoutProductConfigItemSchema).optional(),
    subscriptionProducts: z2.array(cmsCheckoutProductConfigItemSchema).optional()
  }),
  sections: z2.array(cmsCheckoutSectionSchema)
});

// src/checkout/configs/validateCheckout.ts
var crmSchema = deepStrict(crmCheckoutConfigSchema);
var cmsSchema = deepStrict(cmsCheckoutConfigSchema);
var sectionsUnion = cmsCheckoutConfigSchema.shape.sections.element;
var sectionSchemaByType = Object.fromEntries(
  sectionsUnion.options.map((option) => {
    var _a;
    const strict = deepStrict(option);
    const typeField = (_a = strict.shape) == null ? void 0 : _a.type;
    if (typeField instanceof z3.ZodLiteral) {
      return [String(typeField.value), strict];
    }
    return null;
  }).filter((entry) => entry !== null)
);
var checkoutStructureSchema = z3.object({ cms: cmsSchema, crm: crmSchema });
var businessRulesSchema = z3.object({ cms: z3.unknown(), crm: z3.unknown() }).superRefine(({ crm: rawErrorsCrm }, ctx) => {
  const crm = rawErrorsCrm;
  if (crm.isWarrantyPreselected && !crm.isWarrantyEnabled) {
    ctx.addIssue({
      code: "custom",
      path: ["crm", "isWarrantyPreselected"],
      message: "isWarrantyPreselected requires isWarrantyEnabled to be true"
    });
  }
}).superRefine(({ crm: rawErrorsCrm }, ctx) => {
  const crm = rawErrorsCrm;
  if (!crm.isDiscountCodeFeatureEnabled) return;
  if (!crm.discountCode)
    ctx.addIssue({
      code: "custom",
      path: ["crm", "discountCode"],
      message: "discountCode is required when isDiscountCodeFeatureEnabled is true"
    });
  const checkPromo = (products, label) => products == null ? void 0 : products.forEach((p, i) => {
    if (p.promoDiscountedPrice == null)
      ctx.addIssue({
        code: "custom",
        path: ["crm", "products", label, i, "promoDiscountedPrice"],
        message: `promoDiscountedPrice is required on ${label}[${i}] when isDiscountCodeFeatureEnabled is true`
      });
  });
  checkPromo(crm.products.baseProducts, "baseProducts");
  checkPromo(crm.products.subscriptionProducts, "subscriptionProducts");
}).superRefine(({ crm: rawErrorsCrm }, ctx) => {
  const crm = rawErrorsCrm;
  if (!crm.isWarrantyEnabled) return;
  const checkWarranty = (products, label) => products == null ? void 0 : products.forEach((p, i) => {
    if (p.warrantyPrice == null)
      ctx.addIssue({
        code: "custom",
        path: ["crm", "products", label, i, "warrantyPrice"],
        message: `warrantyPrice is required on ${label}[${i}] when isWarrantyEnabled is true`
      });
  });
  checkWarranty(crm.products.baseProducts, "baseProducts");
  checkWarranty(crm.products.subscriptionProducts, "subscriptionProducts");
}).superRefine(({ cms: rawErrorsCms, crm: rawErrorsCrm }, ctx) => {
  var _a, _b, _c, _d;
  const cms = rawErrorsCms;
  const crm = rawErrorsCrm;
  if (crm.isSubscriptionEnabled) {
    if (!((_a = crm.products.subscriptionProducts) == null ? void 0 : _a.length))
      ctx.addIssue({
        code: "custom",
        path: ["crm", "products", "subscriptionProducts"],
        message: "subscriptionProducts are required when isSubscriptionEnabled is true"
      });
    if (!((_b = cms.products.subscriptionProducts) == null ? void 0 : _b.length))
      ctx.addIssue({
        code: "custom",
        path: ["cms", "products", "subscriptionProducts"],
        message: "subscriptionProducts are required in CMS when isSubscriptionEnabled is true"
      });
  } else {
    if (!((_c = crm.products.baseProducts) == null ? void 0 : _c.length))
      ctx.addIssue({
        code: "custom",
        path: ["crm", "products", "baseProducts"],
        message: "baseProducts are required when isSubscriptionEnabled is false"
      });
    if (!((_d = cms.products.baseProducts) == null ? void 0 : _d.length))
      ctx.addIssue({
        code: "custom",
        path: ["cms", "products", "baseProducts"],
        message: "baseProducts are required in CMS when isSubscriptionEnabled is false"
      });
  }
}).superRefine(({ cms: rawErrorsCms, crm: rawErrorsCrm }, ctx) => {
  var _a, _b;
  const cms = rawErrorsCms;
  const crm = rawErrorsCrm;
  const cmsKeys = Object.keys((_a = cms.products.productVariants) != null ? _a : {});
  const crmKeys = Object.keys((_b = crm.products.productVariants) != null ? _b : {});
  const crmKeySet = new Set(crmKeys);
  const cmsKeySet = new Set(cmsKeys);
  cmsKeys.filter((k) => !crmKeySet.has(k)).forEach(
    (k) => ctx.addIssue({
      code: "custom",
      path: ["cms", "products", "productVariants"],
      message: `Variant group "${k}" exists in CMS but not in CRM`
    })
  );
  crmKeys.filter((k) => !cmsKeySet.has(k)).forEach(
    (k) => ctx.addIssue({
      code: "custom",
      path: ["crm", "products", "productVariants"],
      message: `Variant group "${k}" exists in CRM but not in CMS`
    })
  );
  cmsKeys.filter((k) => crmKeySet.has(k)).forEach((key) => {
    const cmsIds = cms.products.productVariants[key].variants.map((v) => v.id);
    const crmIds = crm.products.productVariants[key].variants.map((v) => v.id);
    const crmIdSet = new Set(crmIds);
    const cmsIdSet = new Set(cmsIds);
    cmsIds.filter((id) => !crmIdSet.has(id)).forEach(
      (id) => ctx.addIssue({
        code: "custom",
        path: ["cms", "products", "productVariants", key, "variants"],
        message: `Variant id "${id}" in group "${key}" exists in CMS but not in CRM`
      })
    );
    crmIds.filter((id) => !cmsIdSet.has(id)).forEach(
      (id) => ctx.addIssue({
        code: "custom",
        path: ["crm", "products", "productVariants", key, "variants"],
        message: `Variant id "${id}" in group "${key}" exists in CRM but not in CMS`
      })
    );
  });
  const checkProductIds = (cmsProducts, crmProducts, label) => {
    var _a2, _b2;
    const cmsIds = (_a2 = cmsProducts == null ? void 0 : cmsProducts.map((p) => String(p.id))) != null ? _a2 : [];
    const crmIds = (_b2 = crmProducts == null ? void 0 : crmProducts.map((p) => String(p.id))) != null ? _b2 : [];
    const crmIdSet = new Set(crmIds);
    const cmsIdSet = new Set(cmsIds);
    cmsIds.filter((id) => !crmIdSet.has(id)).forEach(
      (id) => ctx.addIssue({
        code: "custom",
        path: ["cms", "products", label],
        message: `Product id "${id}" exists in CMS ${label} but not in CRM`
      })
    );
    crmIds.filter((id) => !cmsIdSet.has(id)).forEach(
      (id) => ctx.addIssue({
        code: "custom",
        path: ["crm", "products", label],
        message: `Product id "${id}" exists in CRM ${label} but not in CMS`
      })
    );
  };
  checkProductIds(cms.products.baseProducts, crm.products.baseProducts, "baseProducts");
  checkProductIds(
    cms.products.subscriptionProducts,
    crm.products.subscriptionProducts,
    "subscriptionProducts"
  );
});
function buildSchemaByType(union) {
  const u = union;
  if (!(u instanceof z3.ZodUnion)) return {};
  return Object.fromEntries(
    u.options.map((option) => {
      var _a;
      const strict = deepStrict(option);
      const typeField = (_a = strict.shape) == null ? void 0 : _a.type;
      if (typeField instanceof z3.ZodLiteral) {
        return [String(typeField.value), strict];
      }
      return null;
    }).filter((e) => e !== null)
  );
}
function validateByType(items, schemaByType, basePath, errors) {
  items.forEach((item, i) => {
    var _a;
    const type = String((_a = item == null ? void 0 : item.type) != null ? _a : "");
    const schema = schemaByType[type];
    if (!schema) {
      errors.push(`[${[...basePath, i].join(".")}] Unknown type: "${type}"`);
      return;
    }
    const result = schema.safeParse(item);
    if (!result.success) {
      result.error.issues.forEach((issue) => {
        if (issue.message === "Invalid input" && issue.path.length === 0) return;
        const path = [...basePath, `${i}(${type})`, ...issue.path].join(".");
        errors.push(`[${path}] ${issue.message}`);
      });
    }
  });
}
function validateSections(sections) {
  const errors = [];
  sections.forEach((section, i) => {
    var _a;
    const type = String((_a = section == null ? void 0 : section.type) != null ? _a : "");
    const schema = sectionSchemaByType[type];
    if (!schema) {
      errors.push(`[cms.sections.${i}] Unknown section type: "${type}"`);
      return;
    }
    const sectionObj = section;
    const basePath = ["cms", "sections", `${i}(${type})`];
    const result = schema.safeParse(section);
    if (!result.success) {
      result.error.issues.forEach((issue) => {
        const isSlotUnionError = issue.message === "Invalid input" && (issue.path[0] === "slots" || issue.path[0] === "elements");
        if (!isSlotUnionError) {
          errors.push(`[${[...basePath, ...issue.path].join(".")}] ${issue.message}`);
        }
      });
    }
    const slots = sectionObj.slots;
    slots == null ? void 0 : slots.forEach((slot, si) => {
      var _a2, _b, _c, _d;
      const elements = (_a2 = slot.elements) != null ? _a2 : [];
      const slotSchema = (_b = schema.shape) == null ? void 0 : _b.slots;
      if (!slotSchema) return;
      const elementUnion = (_d = (_c = slotSchema.element.shape) == null ? void 0 : _c.elements) == null ? void 0 : _d.element;
      if (elementUnion) {
        const elementSchemaByType = buildSchemaByType(elementUnion);
        validateByType(
          elements,
          elementSchemaByType,
          [...basePath, "slots", si, "elements"],
          errors
        );
      }
    });
  });
  return errors;
}
function validateCmsConfig(cmsConfig) {
  const rawErrors = [];
  const schemaResult = cmsSchema.safeParse(cmsConfig);
  if (!schemaResult.success) {
    schemaResult.error.issues.forEach((issue) => {
      const isSectionUnionError = issue.path[0] === "sections" && typeof issue.path[1] === "number" && issue.message === "Invalid input";
      if (!isSectionUnionError) {
        rawErrors.push(`[${issue.path.join(".")}] ${issue.message}`);
      }
    });
  }
  const sections = cmsConfig == null ? void 0 : cmsConfig.sections;
  if (Array.isArray(sections)) {
    rawErrors.push(...validateSections(sections));
  }
  if (rawErrors.length === 0) return { valid: true };
  return { valid: false, errors: makeErrors(rawErrors) };
}
function validateCheckoutConfigs(cmsConfig, crmConfig) {
  const rawErrors = [];
  const schemaResult = checkoutStructureSchema.safeParse({ cms: cmsConfig, crm: crmConfig });
  if (!schemaResult.success) {
    schemaResult.error.issues.forEach((issue) => {
      const isSectionUnionError = issue.path[0] === "cms" && issue.path[1] === "sections" && typeof issue.path[2] === "number" && issue.message === "Invalid input";
      if (!isSectionUnionError) {
        rawErrors.push(`[${issue.path.join(".")}] ${issue.message}`);
      }
    });
  }
  const rulesResult = businessRulesSchema.safeParse({ cms: cmsConfig, crm: crmConfig });
  if (!rulesResult.success) {
    rulesResult.error.issues.forEach(
      (issue) => rawErrors.push(`[${issue.path.join(".")}] ${issue.message}`)
    );
  }
  const sections = cmsConfig == null ? void 0 : cmsConfig.sections;
  if (Array.isArray(sections)) {
    rawErrors.push(...validateSections(sections));
  }
  if (rawErrors.length === 0) return { valid: true };
  return { valid: false, errors: makeErrors(rawErrors) };
}

export {
  PRODUCT_HERO_MEDIA_KIND,
  toAdminMessage,
  makeErrors,
  validateCmsConfig,
  validateCheckoutConfigs
};
//# sourceMappingURL=chunk-QINU3K7Q.mjs.map